/**
 * Front-end wire types for VDS-3389 (In-App Campaigns).
 *
 * Mirrors the monorepo's cross-repo contract exactly (`design.md` §"Cross-repo contract"):
 * `GET /api/v2/in-app-campaigns/admin/next` responds `200` with `data: InAppCampaignModal | null`
 * — the empty-result contract is `data === null`, never a `204` status code.
 */

export enum InAppCampaignMediaType {
  image = 'image',
  video = 'video',
  none = 'none',
}

export enum InAppCampaignActionType {
  link = 'link',
  none = 'none',
}

export interface InAppCampaignMedia {
  type: InAppCampaignMediaType;
  /** Absent when `type === 'none'`. */
  url?: string;
  width?: number;
  height?: number;
}

export interface InAppCampaignAction {
  type: InAppCampaignActionType;
  buttonLabel?: string;
  /** Absent when `type === 'none'`. */
  url?: string;
}

/** `data` payload of `GET /admin/next` — what the admin-facing modal renders. */
export interface InAppCampaignModal {
  id: string;
  badgeLabel?: string;
  title: string;
  description: string;
  media: InAppCampaignMedia;
  action: InAppCampaignAction;
}

/** Why the modal closed — drives fire-and-forget interaction reporting (MOD-5). */
export enum InAppCampaignCloseReason {
  click = 'click',
  dismiss = 'dismiss',
}

export interface InAppCampaignCloseEvent {
  reason: InAppCampaignCloseReason;
  actionType?: InAppCampaignActionType;
}

/**
 * PR4a (super-admin console list) types below — `design.md` §"Cross-repo contract".
 */

export enum CampaignDisplayStatus {
  draft = 'draft',
  scheduled = 'scheduled',
  active = 'active',
  finished = 'finished',
}

/** Status filter chip value — `all` omits `displayStatus` from the request entirely. */
export enum InAppCampaignStatusFilter {
  all = 'all',
  draft = 'draft',
  scheduled = 'scheduled',
  active = 'active',
  finished = 'finished',
}

/**
 * Every chip but `all` maps 1:1 onto a display status; `all` maps to `undefined`, which is what
 * drops the query param. Written as an exhaustive record rather than a cast so adding a chip
 * without deciding what it filters by is a compile error, not a silently unfiltered request.
 */
export const DISPLAY_STATUS_BY_FILTER: Readonly<
  Record<InAppCampaignStatusFilter, CampaignDisplayStatus | undefined>
> = {
  [InAppCampaignStatusFilter.all]: undefined,
  [InAppCampaignStatusFilter.draft]: CampaignDisplayStatus.draft,
  [InAppCampaignStatusFilter.scheduled]: CampaignDisplayStatus.scheduled,
  [InAppCampaignStatusFilter.active]: CampaignDisplayStatus.active,
  [InAppCampaignStatusFilter.finished]: CampaignDisplayStatus.finished,
};

export interface InAppCampaignMetrics {
  views: number;
  uniqueUsers: number;
  clicks: number;
  /** Computed server-side only — the dashboard performs no metric arithmetic. */
  ctr: number;
}

/** A row of `GET /super-admin`'s `Paginate<InAppCampaignListItem>`. */
export interface InAppCampaignListItem {
  id: string;
  internalName: string;
  /** Omitted by the backend in phase 1 — never assume presence. */
  audienceSummary?: string;
  /** Omitted by the backend in phase 1 — never assume presence. */
  icon?: string;
  displayStatus: CampaignDisplayStatus;
  metrics: InAppCampaignMetrics;
  startAt: string;
  endAt: string;
  /** Server-computed (`displayStatus ∈ {active,scheduled}`) — never re-derived in the front (RN-2). */
  canFinish: boolean;
}

/** `data` payload of `GET /super-admin/summary` — all 5 fields are server-computed. */
export interface InAppCampaignSummary {
  activeCampaigns: number;
  totalCampaigns: number;
  totalViews: number;
  totalClicks: number;
  avgCtr: number;
}

/**
 * mongoose-paginate-v2 shape. Non-negotiable: the dashboard reads `totalDocs`/`hasNextPage`
 * only, never `total`/`rowsPerPage` — mistyping this has broken paginators in this repo before.
 */
export interface Paginate<T> {
  docs: T[];
  totalDocs: number;
  limit: number;
  page: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

/**
 * PR4b-1 (console form) types below — `design.md` §"Form".
 */

/**
 * Frequency window. Deliberately NOT `TimeLapses` (`interface/data.interface.ts`): that one is the
 * chart-interval enum — it carries `today`/`year`, which are not valid campaign windows, and lacks
 * `total`, which is a lifetime cap rather than a unit of time.
 */
export enum InAppCampaignFrequencyPeriod {
  day = 'day',
  week = 'week',
  month = 'month',
  total = 'total',
}

/** `201 { url, size, filename, width: 1440, height: 810 }` from `POST /super-admin/media`. */
export interface CampaignImageUploadResult {
  url: string;
  size: number;
  filename: string;
  width: number;
  height: number;
}

/** `error.code` of the `422` that `POST /super-admin/media` answers for a wrong-sized image. */
export const CAMPAIGN_IMAGE_INVALID_DIMENSIONS_CODE = 'InAppCampaignModule.errors.in-app-campaign-image-invalid-dimensions';

/** A `{ width, height }` pair as the media endpoint reports it. */
export interface CampaignImageDimensions {
  width: number;
  height: number;
}

/**
 * `error.data` of that `422`. This endpoint is the exception: it sends a real JSON object, while
 * most other endpoints of this API put a plain string in the same slot — and an older backend
 * still deployed on dev sends nothing at all. Hence `unknown`-ish members: read it through
 * `CampaignMediaSectionComponent`'s shape guard, never by trusting the declaration.
 */
export interface CampaignImageInvalidDimensionsData {
  expected?: CampaignImageDimensions;
  received?: CampaignImageDimensions;
}

/** The `{ success: false, error: { code, message, data } }` body of a failed media upload. */
export interface CampaignImageUploadErrorBody {
  success?: boolean;
  httpStatus?: number;
  error?: {
    code?: string;
    message?: string;
    data?: CampaignImageInvalidDimensionsData | string | null;
  };
}

/**
 * PR4b-2 (audience, publish, preview) types below — `design.md` §"Cross-repo contract" +
 * `specs/in-app-campaign-audience/spec.md`.
 */

export enum InAppCampaignAudienceScope {
  all = 'all',
  segment = 'segment',
}

/**
 * RN-3: `scope='all'` + `excludedBuildingIds=[]` means "every building selected". `planIds` is
 * intentionally NOT part of this shape — the backend always forces it to `[]` in phase 1
 * (AUD-4), so the dashboard never sends or reads it.
 */
export interface InAppCampaignAudience {
  scope: InAppCampaignAudienceScope;
  countryIds: string[];
  buildingCompanyIds: string[];
  excludedBuildingIds: string[];
}

/** A row of `GET /super-admin/audience/buildings` (AUD-6). */
export interface AudienceBuilding {
  id: string;
  name: string;
  country: string;
  countryId: string | null;
  company: string;
  companyId: string | null;
  units: number;
}

/**
 * One selectable value of a segment dimension, with the buildings behind it. The count is what
 * makes a chip decidable — "Panamá 157" says what the campaign is about to reach, "Panamá" alone
 * does not.
 */
export interface AudienceFacet {
  /** A real id, or `SELF_MANAGED_COMPANY_ID` for the "no administradora" bucket. */
  id: string;
  /** Empty for the self-managed bucket — the dashboard labels that one itself. */
  name: string;
  buildings: number;
}

/**
 * `data` payload of `GET /super-admin/audience/facets` — the segment values that active buildings
 * actually have. NOT a catalogue: `countries` in Mongo is the ~250-row world list and offering all
 * of it would let an admin pick a segment that can only ever reach zero buildings.
 */
export interface AudienceFacets {
  totalBuildings: number;
  countries: AudienceFacet[];
  companies: AudienceFacet[];
}

/**
 * The "no administradora" bucket, selectable like any company — most buildings are self-managed,
 * so a company filter unable to express "none" could not reach them at all. Must stay identical to
 * the monorepo's `SELF_MANAGED_COMPANY_ID`; a 24-hex ObjectId can never collide with it.
 */
export const SELF_MANAGED_COMPANY_ID = 'self-managed';

/** `data` payload of `GET /super-admin/audience/preview` — building count only, no `units`. */
export interface AudiencePreviewResult {
  buildings: number;
}

export interface InAppCampaignFormPayload {
  internalName: string;
  title: string;
  description: string;
  media: InAppCampaignMedia;
  action: InAppCampaignAction;
  audience: InAppCampaignAudience;
  frequency: { times: number; period: InAppCampaignFrequencyPeriod };
  startAt: string;
  endAt: string;
}

/**
 * A draft may legitimately be saved with most fields still blank — the completeness gate runs at
 * publish, not at save. The console omits every blank value from the draft request instead of
 * sending empty strings the server would reject.
 */
export type InAppCampaignDraftPayload = Partial<InAppCampaignFormPayload>;

/**
 * Persisted lifecycle state — drives the AUTH-4 field locking in the console form.
 *
 * Deliberately NOT `CampaignDisplayStatus`: that one is derived server-side from this state plus
 * the schedule window, so it carries `scheduled`/`active` — which `state` never has — while a
 * `published` campaign has no display status of its own. The two overlap only on the `draft` and
 * `finished` labels and must never be compared against each other.
 */
export enum InAppCampaignState {
  draft = 'draft',
  published = 'published',
  finished = 'finished',
}

/**
 * `data` payload of `GET /super-admin/:id` — mirrors the monorepo's
 * `in-app-campaign-detail.response.dto.ts` exactly.
 *
 * Every `Date` on that DTO crosses the wire as an ISO 8601 string, so the date fields are typed
 * `string` here. `endAt` is nullable on purpose: a draft can be saved before an end date exists.
 */
export interface InAppCampaignDetail {
  id: string;
  internalName: string;
  title: string;
  description: string;
  badgeLabel?: string;
  media: InAppCampaignMedia;
  action: InAppCampaignAction;
  /** Carries `planIds` on the wire; the dashboard neither reads nor sends it (AUD-4). */
  audience: InAppCampaignAudience;
  frequency: { times: number; period: InAppCampaignFrequencyPeriod };
  state: InAppCampaignState;
  displayStatus: CampaignDisplayStatus;
  canFinish: boolean;
  startAt: string;
  endAt: string | null;
  createdAt: string;
  finishedAt: string | null;
  finishedBy: string | null;
  metrics: InAppCampaignMetrics;
}

export interface InAppCampaignMutationResult {
  id: string;
}

export interface InAppCampaignPublishResult {
  id: string;
  displayStatus: CampaignDisplayStatus;
}
